import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { buildTagLookup } from "@/lib/tags";
import { useAuth } from "@/context/AuthContext";

const TagsContext = createContext(null);

/**
 * Centralized cache of the current PRO user's tags. Standard users skip the
 * fetch entirely — every consumer will get an empty array.
 */
export const TagsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const isPro = user?.account_type === "pro";
  const [tags, setTags] = useState([]);
  const [palette, setPalette] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !isPro) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get("/tags");
      setTags(data.tags || []);
      setPalette(data.palette || []);
    } catch (e) {
      // silent — the sidebar shouldn't be spammy about tag load failures
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isPro]);

  useEffect(() => { refresh(); }, [refresh]);

  const createTag = useCallback(async ({ name, color }) => {
    const { data } = await apiClient.post("/tags", { name, color });
    setTags((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  }, []);

  const updateTag = useCallback(async (id, patch) => {
    const { data } = await apiClient.patch(`/tags/${id}`, patch);
    setTags((prev) => prev
      .map((t) => (t.id === id ? { ...t, ...data } : t))
      .sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  }, []);

  const deleteTag = useCallback(async (id) => {
    await apiClient.delete(`/tags/${id}`);
    setTags((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const setDebtorTags = useCallback(async (debtorId, tagIds) => {
    const { data } = await apiClient.post(`/debtors/${debtorId}/tags`, { tag_ids: tagIds });
    // Refresh counts (they might change).
    refresh();
    return data;
  }, [refresh]);

  const lookup = useMemo(() => buildTagLookup(tags), [tags]);

  const value = useMemo(() => ({
    tags, lookup, palette, loading,
    refresh, createTag, updateTag, deleteTag, setDebtorTags,
    isPro,
  }), [tags, lookup, palette, loading, refresh, createTag, updateTag, deleteTag, setDebtorTags, isPro]);

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
};

export const useTags = () => {
  const ctx = useContext(TagsContext);
  if (!ctx) {
    // Non-provider fallback so Standard users don't crash tag-aware components.
    return {
      tags: [], lookup: new Map(), palette: [], loading: false, isPro: false,
      refresh: async () => {},
      createTag: async () => { throw new Error("Tags are PRO-only"); },
      updateTag: async () => { throw new Error("Tags are PRO-only"); },
      deleteTag: async () => { throw new Error("Tags are PRO-only"); },
      setDebtorTags: async () => { throw new Error("Tags are PRO-only"); },
    };
  }
  return ctx;
};
